import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ListChecks, ReceiptIndianRupee, KeyRound, Megaphone, Truck, Link2, Copy, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { StatusBadge, PriorityBadge, Chip, SectionLabel } from "@/components/Bits";
import AuthImage from "@/components/AuthImage";
import { formatINR, shipmentClasses } from "@/lib/ui";
import api from "@/lib/api";

function Panel({ title, icon: Icon, count, children }) {
  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-zinc-400" />
        <h3 className="font-display text-lg font-medium">{title}</h3>
        <span className="text-xs font-mono text-zinc-600">({count})</span>
      </div>
      {children}
    </div>
  );
}

export default function ClientWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [portalOpen, setPortalOpen] = useState(false);
  const [portalToken, setPortalToken] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/clients/${id}`).then((r) => setClient(r.data)).catch(() => navigate("/clients"));
  }, [id, navigate]);

  const openPortal = async () => {
    try {
      const { data } = await api.post(`/clients/${id}/portal`);
      setPortalToken(data.portal_token);
      setPortalOpen(true);
    } catch (e) { toast.error("Could not generate portal link"); }
  };

  const portalUrl = portalToken ? `${window.location.origin}/portal/${portalToken}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(portalUrl);
    } catch (e) {
      const el = document.querySelector('[data-testid="portal-link-input"]');
      if (el) { el.select(); try { document.execCommand("copy"); } catch (err) { console.error("clipboard fallback failed", err); } }
    }
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const revokeLink = async () => {
    await api.delete(`/clients/${id}/portal`);
    setPortalOpen(false);
    setPortalToken("");
    toast.success("Portal link revoked");
  };

  if (!client) return <div className="p-8 text-zinc-500 font-mono text-sm">Loading workspace…</div>;

  const billed = (client.invoices || []).reduce((s, i) => s + (i.amount || 0), 0);
  const received = (client.invoices || []).reduce((s, i) => s + (i.received || 0), 0);

  return (
    <div>
      <PageHeader
        title={client.brand}
        subtitle={`${client.industry || "—"} · AM: ${client.account_manager || "Unassigned"}`}
        actions={
          <div className="flex items-center gap-3">
            <button data-testid="portal-link-button" onClick={openPortal} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">
              <Link2 size={15} /> Client Portal
            </button>
            <button data-testid="back-to-clients" onClick={() => navigate("/clients")} className="flex items-center gap-2 text-sm border border-white/15 text-zinc-300 hover:bg-white/5 px-4 py-2 rounded-sm transition-colors">
              <ArrowLeft size={15} /> All Clients
            </button>
          </div>
        }
      />
      <div className="p-8 space-y-6">
        {/* header strip */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
            <SectionLabel>Status</SectionLabel>
            <div className="mt-2"><StatusBadge status={client.status} /></div>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
            <SectionLabel>Active Tasks</SectionLabel>
            <div className="font-display text-3xl font-bold mt-1">{(client.tasks || []).filter((t) => t.status !== "Completed").length}</div>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
            <SectionLabel>Billed</SectionLabel>
            <div className="font-display text-2xl font-bold mt-1">{formatINR(billed)}</div>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
            <SectionLabel>Received</SectionLabel>
            <div className="font-display text-2xl font-bold mt-1 text-[#00FF66]">{formatINR(received)}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {(client.services || []).map((s) => (
            <span key={s} className="text-[10px] font-mono uppercase tracking-wider border border-white/10 bg-white/[0.03] text-zinc-400 px-2.5 py-1 rounded-sm">{s}</span>
          ))}
        </div>

        {/* Tasks */}
        <Panel title="Tasks" icon={ListChecks} count={(client.tasks || []).length}>
          {(client.tasks || []).length === 0 ? (
            <p className="text-sm text-zinc-500">No tasks yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-white/10">
                    {["ID", "Division", "Deliverable", "Owner", "Priority", "Status"].map((h) => (
                      <th key={h} className="py-2 px-2 font-mono text-[11px] uppercase tracking-wider text-zinc-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {client.tasks.map((t) => (
                    <tr key={t.id} onClick={() => navigate("/tasks")} className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors">
                      <td className="py-2.5 px-2 font-mono text-xs text-zinc-500">{t.ref}</td>
                      <td className="py-2.5 px-2 text-zinc-400 text-xs">{t.division}</td>
                      <td className="py-2.5 px-2 text-zinc-300 max-w-[200px] truncate">{t.title}</td>
                      <td className="py-2.5 px-2 text-zinc-400">{t.assigned_to || "—"}</td>
                      <td className="py-2.5 px-2"><PriorityBadge priority={t.priority} /></td>
                      <td className="py-2.5 px-2"><StatusBadge status={t.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Invoices */}
          <Panel title="Invoices" icon={ReceiptIndianRupee} count={(client.invoices || []).length}>
            {(client.invoices || []).length === 0 ? (
              <p className="text-sm text-zinc-500">No invoices.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {client.invoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-white/5">
                      <td className="py-2.5 text-zinc-300">{inv.service || "Invoice"}</td>
                      <td className="py-2.5 font-mono text-zinc-200 text-right">{formatINR(inv.amount)}</td>
                      <td className="py-2.5 text-right pl-3"><Chip className={inv.status === "Paid" ? "bg-[#00FF66]/10 border-[#00FF66]/30 text-[#00FF66]" : inv.status === "Partial" ? "bg-[#FFD600]/10 border-[#FFD600]/30 text-[#FFD600]" : "bg-[#FF3B30]/10 border-[#FF3B30]/30 text-[#FF6259]"}>{inv.status}</Chip></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>

          {/* Vault */}
          <Panel title="Access Vault" icon={KeyRound} count={(client.vault || []).length}>
            {(client.vault || []).length === 0 ? (
              <p className="text-sm text-zinc-500">No vault references.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {client.vault.map((v) => (
                    <tr key={v.id} className="border-b border-white/5">
                      <td className="py-2.5 text-zinc-300">{v.platform}</td>
                      <td className="py-2.5 font-mono text-xs text-[#4DA3FF]">{v.vault_ref}</td>
                      <td className="py-2.5 text-right"><Chip className={v.two_fa === "Enabled" ? "bg-[#00FF66]/10 border-[#00FF66]/30 text-[#00FF66]" : "bg-[#FF3B30]/10 border-[#FF3B30]/30 text-[#FF6259]"}>{v.two_fa}</Chip></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </div>

        {/* Social */}
        <Panel title="Social Content" icon={Megaphone} count={(client.social || []).length}>
          {(client.social || []).length === 0 ? (
            <p className="text-sm text-zinc-500">No social posts.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {client.social.map((p) => (
                <div key={p.id} className="bg-[#0a0a0a] border border-white/10 rounded-sm overflow-hidden">
                  {p.image_path ? (
                    <AuthImage path={p.image_path} className="w-full h-28 object-cover" />
                  ) : (
                    <div className="w-full h-28 das-grid flex items-center justify-center text-zinc-700 text-[10px] font-mono">No image</div>
                  )}
                  <p className="text-xs text-zinc-400 p-2 line-clamp-3">{p.caption}</p>
                </div>
              ))}
            </div>
          )}
        </Panel>
        {/* Shipments */}
        <Panel title="Shipments" icon={Truck} count={(client.shipments || []).length}>
          {(client.shipments || []).length === 0 ? (
            <p className="text-sm text-zinc-500">No shipments.</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {client.shipments.map((s) => (
                  <tr key={s.id} className="border-b border-white/5">
                    <td className="py-2.5 text-zinc-300">{s.customer_name}</td>
                    <td className="py-2.5 text-zinc-400 text-xs">{s.courier}</td>
                    <td className="py-2.5 font-mono text-xs text-[#4DA3FF]">{s.tracking_number || "—"}</td>
                    <td className="py-2.5 text-right"><Chip className={shipmentClasses(s.status)}>{s.status}</Chip></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>

      <Dialog open={portalOpen} onOpenChange={setPortalOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Client Portal Link</DialogTitle>
            <DialogDescription className="sr-only">Private read-only link for the client to view status and approve work.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-sm text-zinc-400">Share this private link with <span className="text-white">{client.brand}</span>. They can view live project status and approve or request revisions — no login needed.</p>
            <div className="flex items-center gap-2">
              <input data-testid="portal-link-input" readOnly value={portalUrl} className="flex-1 bg-[#050505] border border-white/10 text-[#4DA3FF] rounded-sm px-3 py-2 text-sm font-mono" />
              <button data-testid="copy-portal-link" onClick={copyLink} className="flex items-center gap-1.5 text-sm bg-white text-black font-medium px-3 py-2 rounded-sm hover:bg-zinc-200 transition-colors">
                {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <a href={portalUrl} target="_blank" rel="noreferrer" className="inline-block text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-white">Open portal preview →</a>
            <button data-testid="revoke-portal-link" onClick={revokeLink} className="block text-xs font-mono uppercase tracking-wider text-[#FF6259] hover:text-[#FF8078] mt-2">Revoke this link</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
